import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import { filterVisibleInterestRequests } from "@/pages/interests/components/InterestRequestsPanel";
import { useUserContext } from "@/context/UserContext";
import { useCollaborationEngagementPayment } from "@/pages/interests/hooks/useCollaborationEngagementPayment";
import { navigateToCompanyWorkspace } from "@/shared/utils/collaborationNavigation.util";
import type { RequestFilter } from "@/pages/dashboard/dashboardTab.const";
import type { CompanyProfileLinkState } from "@/shared/utils/companyProfileLink.util";

type UseInterestRequestsOptions = {
  /** Refetch received/sent when the request filter changes; null skips filter-driven refetch. */
  activeRequestFilter?: RequestFilter | null;
  scrollOnFilterChange?: boolean;
  /** Router state for workspace back navigation (e.g. dashboard requests tab). */
  workspaceLinkState?: CompanyProfileLinkState;
};

export function useInterestRequests(options: UseInterestRequestsOptions = {}) {
  const { activeRequestFilter = null, scrollOnFilterChange = true, workspaceLinkState } = options;
  const navigate = useNavigate();
  const { refreshPendingInterestCount, refreshCollaborationSidebarCounts } = useUserContext();

  const [incomingRequests, setIncomingRequests] = useState<any[]>([]);
  const [incomingPage, setIncomingPage] = useState(1);
  const [incomingTotalPages, setIncomingTotalPages] = useState(1);
  const [incomingTotalCount, setIncomingTotalCount] = useState(0);
  const [loadingIncoming, setLoadingIncoming] = useState(true);

  const [sentRequests, setSentRequests] = useState<any[]>([]);
  const [sentPage, setSentPage] = useState(1);
  const [sentTotalPages, setSentTotalPages] = useState(1);
  const [sentTotalCount, setSentTotalCount] = useState(0);
  const [loadingSent, setLoadingSent] = useState(true);

  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchIncoming = useCallback(async () => {
    setLoadingIncoming(true);
    try {
      const response = await trpc.collaboration.getReceived.query({ page: incomingPage, limit: 10 });
      const data = Array.isArray(response) ? response : response?.matches || [];
      const visible = filterVisibleInterestRequests(data, false);
      setIncomingRequests(visible);
      setIncomingTotalPages(visible.length > 0 ? 1 : 1);
      setIncomingTotalCount(visible.length);
    } catch (err) {
      handleTrpcError(err);
      setIncomingRequests([]);
    } finally {
      setLoadingIncoming(false);
    }
  }, [incomingPage]);

  const fetchSent = useCallback(async () => {
    setLoadingSent(true);
    try {
      const response = await trpc.collaboration.getSent.query({ page: sentPage, limit: 10 });
      const data = Array.isArray(response) ? response : response?.matches || [];
      const visible = filterVisibleInterestRequests(data, true);
      setSentRequests(visible);
      setSentTotalPages(visible.length > 0 ? 1 : 1);
      setSentTotalCount(visible.length);
    } catch (err) {
      handleTrpcError(err);
      setSentRequests([]);
    } finally {
      setLoadingSent(false);
    }
  }, [sentPage]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      fetchIncoming();
      fetchSent();
      isInitialMount.current = false;
      return;
    }

    if (activeRequestFilter === "received") {
      if (scrollOnFilterChange) window.scrollTo({ top: 0, behavior: "smooth" });
      fetchIncoming();
    } else if (activeRequestFilter === "sent") {
      if (scrollOnFilterChange) window.scrollTo({ top: 0, behavior: "smooth" });
      fetchSent();
    } else if (activeRequestFilter === "all") {
      if (scrollOnFilterChange) window.scrollTo({ top: 0, behavior: "smooth" });
      fetchIncoming();
      fetchSent();
    }
  }, [activeRequestFilter, fetchIncoming, fetchSent, scrollOnFilterChange]);

  useEffect(() => {
    if (activeRequestFilter === "received" || activeRequestFilter === "all") fetchIncoming();
  }, [incomingPage, activeRequestFilter, fetchIncoming]);

  useEffect(() => {
    if (activeRequestFilter === "sent" || activeRequestFilter === "all") fetchSent();
  }, [sentPage, activeRequestFilter, fetchSent]);

  const refreshAfterEngagementPayment = useCallback(() => {
    fetchIncoming();
    fetchSent();
    refreshPendingInterestCount();
    refreshCollaborationSidebarCounts();
  }, [fetchIncoming, fetchSent, refreshPendingInterestCount, refreshCollaborationSidebarCounts]);

  const {
    engagementFeeCredits,
    paymentModalOpened,
    paymentLoading,
    beginEngagementPayment,
    handlePaymentModalClose,
    handlePaymentConfirm,
    successData,
    handleSuccessModalClose,
  } = useCollaborationEngagementPayment(refreshAfterEngagementPayment);

  const handleAccept = async (matchId: string) => {
    setActionLoading(matchId);
    try {
      await trpc.collaboration.accept.mutate({ matchId });
      successNotification({ title: "Success", message: "Collaboration request accepted!" });
      refreshPendingInterestCount();
      refreshCollaborationSidebarCounts();
      navigate(`/collaborations/${matchId}`, { state: workspaceLinkState });
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (matchId: string) => {
    setActionLoading(matchId);
    try {
      await trpc.collaboration.reject.mutate({ matchId });
      successNotification({ title: "Success", message: "Collaboration request declined." });
      fetchIncoming();
      refreshPendingInterestCount();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMakePayment = (matchId: string) => {
    beginEngagementPayment(matchId);
  };

  const handleViewProfile = (companyId: string) => {
    void navigateToCompanyWorkspace(navigate, companyId, { state: workspaceLinkState });
  };

  const handleOpenWorkspace = (matchId: string) =>
    navigate(`/collaborations/${matchId}`, { state: workspaceLinkState });

  return {
    incomingRequests,
    incomingPage,
    setIncomingPage,
    incomingTotalPages,
    incomingTotalCount,
    loadingIncoming,
    sentRequests,
    sentPage,
    setSentPage,
    sentTotalPages,
    sentTotalCount,
    loadingSent,
    actionLoading,
    handleAccept,
    handleDecline,
    handleMakePayment,
    handleViewProfile,
    handleOpenWorkspace,
    engagementFeeCredits,
    paymentModalOpened,
    paymentLoading,
    handlePaymentModalClose,
    handlePaymentConfirm,
    successData,
    handleSuccessModalClose,
    fetchIncoming,
    fetchSent,
  };
}
